'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface NavItem {
  label: string;
  href: string;
  icon: string;
  badge?: number;
  group?: string;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/', icon: 'ChartBarIcon', group: 'Monitor' },
  { label: 'Log Activity', href: '/activity-log-entry', icon: 'ClipboardDocumentListIcon', group: 'Monitor' },
  { label: 'Sign In', href: '/sign-up-login-screen', icon: 'ArrowRightOnRectangleIcon', group: 'Account' },
];

const groups = ['Monitor', 'Account'];

interface SidebarProps {
  activePath?: string;
}

export default function Sidebar({ activePath = '/' }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (href: string) => activePath === href;

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-border ${collapsed ? 'justify-center px-2' : ''}`}>
        <AppLogo size={36} />
        {!collapsed && (
          <div className="flex flex-col min-w-0">
            <span className="font-extrabold text-base text-primary leading-tight tracking-tight truncate">
              PrakritiVeer
            </span>
            <span className="text-[10px] text-muted-foreground font-medium leading-tight truncate">
              Respecting Mother Nature
            </span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
        {groups.map((group) => {
          const items = navItems.filter((n) => n.group === group);
          return (
            <div key={`group-${group}`} className="mb-4">
              {!collapsed && (
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-3 mb-2">
                  {group}
                </p>
              )}
              {items.map((item) => (
                <Link
                  key={`nav-${item.href}`}
                  href={item.href}
                  className={`sidebar-nav-item ${isActive(item.href) ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon name={item.icon as any} size={20} variant="outline" className="flex-shrink-0" />
                  {!collapsed && (
                    <span className="flex-1 truncate">{item.label}</span>
                  )}
                  {!collapsed && item.badge !== undefined && item.badge > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent text-accent-foreground text-[10px] font-bold">
                      {item.badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          );
        })}
      </nav>

      {/* Bottom user area */}
      <div className={`border-t border-border px-3 py-4 ${collapsed ? 'flex justify-center' : ''}`}>
        {!collapsed ? (
          <div className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-muted transition-colors cursor-pointer">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-primary">SC</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">State Coordinator</p>
              <p className="text-xs text-muted-foreground truncate">Maharashtra Zone</p>
            </div>
            <Icon name="Cog6ToothIcon" size={16} variant="outline" className="text-muted-foreground flex-shrink-0" />
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center cursor-pointer hover:bg-primary/30 transition-colors">
            <span className="text-xs font-bold text-primary">SC</span>
          </div>
        )}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="hidden lg:flex items-center justify-center w-full py-3 border-t border-border text-muted-foreground hover:text-primary hover:bg-muted/50 transition-colors"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <Icon name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={16} variant="outline" />
        {!collapsed && <span className="ml-2 text-xs font-medium">Collapse</span>}
      </button>
    </div>
  );

  return (
    <>
      {/* Mobile topbar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AppLogo size={30} />
          <span className="font-extrabold text-base text-primary">PrakritiVeer</span>
        </div>
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-lg hover:bg-muted transition-colors"
          aria-label="Open navigation"
        >
          <Icon name="Bars3Icon" size={22} variant="outline" />
        </button>
      </div>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm animate-fade-in"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed top-0 left-0 z-50 h-full w-72 bg-card border-r border-border transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <AppLogo size={32} />
            <span className="font-extrabold text-base text-primary">PrakritiVeer</span>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            aria-label="Close navigation"
          >
            <Icon name="XMarkIcon" size={20} variant="outline" />
          </button>
        </div>
        <nav className="px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={`mobile-nav-${item.href}`}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className={`sidebar-nav-item ${isActive(item.href) ? 'active' : ''}`}
            >
              <Icon name={item.icon as any} size={20} variant="outline" className="flex-shrink-0" />
              <span className="flex-1 truncate">{item.label}</span>
              {item.badge !== undefined && item.badge > 0 && (
                <span className="ml-auto inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent text-accent-foreground text-[10px] font-bold">
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed top-0 left-0 h-full bg-card border-r border-border z-30 transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'}`}
      >
        <SidebarContent />
      </aside>

      {/* Mobile top spacer */}
      <div className="lg:hidden h-14 flex-shrink-0" />
    </>
  );
}